import { ArrowLeft, List, SignIn } from '@phosphor-icons/react';
import { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useScreenView } from '../../utils/ScreenViewContext';

interface DemoTopBarProps {
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  showSideNav: boolean;
  setShowSideNav: (show: boolean) => void;
}

const DemoTopBar = ({
  currentTab,
  setCurrentTab,
  showSideNav,
  setShowSideNav,
}: DemoTopBarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { screenView } = useScreenView();

  const slugToTitle = (s: string) =>
    s.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const looksLikeOpaqueId = (s: string) =>
    /^[0-9a-f-]{10,}$/.test(s) || /^\d{6,}$/.test(s);

  const getPathTitle = (pathname: string) => {
    const trimmed = pathname.replace(/\/+$/, '');
    const segments = trimmed.split('/').filter(Boolean);
    const last = segments[segments.length - 1] ?? '';
    const prev = segments[segments.length - 2] ?? '';
    const base = looksLikeOpaqueId(last) ? prev : last;
    return slugToTitle(base || 'Overview');
  };

  const pathHasSegment = (pathname: string, segment: string) => {
    const segments = pathname.replace(/\/+$/, '').split('/').filter(Boolean);
    return segments.includes(segment);
  };

  const resolveTitle = (pathname: string, fallback: string) => {
    const p = pathname.replace(/\/+$/, '');
    if (/(^|\/)cash-advances(\/|$)/.test(p)) return 'Cash Advances';
    if (p.endsWith('/cash-advance/consent')) return 'Sign Documents';
    if (/(^|\/)cash-advance(\/|$)/.test(p)) return 'Cash Advance Details';
    if (pathHasSegment(p, 'assets') && pathHasSegment(p, 'overview'))
      return 'Asset Overview';
    if (pathHasSegment(p, 'assets') && pathHasSegment(p, 'maintenance'))
      return 'Asset Maintenance';
    if (p.includes('/assets/odometer-history')) return 'Odometer History';
    if (p.includes('submit-mileage')) return 'Submit Mileage';
    if (p.includes('/settings')) return 'Settings';
    if (p.includes('/broker-network')) return 'Broker Network';
    return fallback;
  };

  const getBackConfig = (pathname: string) => {
    const p = pathname.replace(/\/+$/, '');
    const hasSingular = pathHasSegment(p, 'cash-advance');
    const hasPlural = pathHasSegment(p, 'cash-advances');

    if (hasPlural && pathHasSegment(p, 'details')) {
      return {
        title: 'Cash Advance Details',
        to: '/demo/banking/cash-advances/active' as const,
      };
    }
    if (hasSingular && !hasPlural && !p.endsWith('/consent')) {
      return {
        title: 'Request a Cash Advance',
        to: '/demo/banking/cash-advances/active' as const,
      };
    }
    if (hasSingular && p.endsWith('/consent')) {
      return { title: 'Sign Documents', to: -1 as const };
    }
    if (pathHasSegment(p, 'create') && pathHasSegment(p, 'assets')) {
      return { title: 'Add Asset', to: '/demo/assets' as const };
    }
    if (p.includes('/assets/submit-mileage')) {
      return { title: 'Submit Mileage', to: '/demo/assets' as const };
    }
    if (p.includes('/assets/odometer-history')) {
      return {
        title: 'Odometer History',
        to: '/demo/assets/submit-mileage' as const,
      };
    }
    if (p.includes('/broker-network')) {
      return { title: 'Broker Network', to: '/demo/home' as const };
    }
    return null;
  };

  const Title = ({ children }: { children: string }) => (
    <h1 className="text-[30px] font-bold text-primary">{children}</h1>
  );

  const BackTitle = ({
    to,
    children,
  }: {
    to: string | number;
    children: string;
  }) => {
    return (
      <div className="flex flex-row items-center space-x-2 align-middle">
        <ArrowLeft
          size={screenView === 'mobile' ? 20 : 24}
          className="text-primary hover:cursor-pointer"
          onClick={() =>
            typeof to === 'number' ? navigate(to) : navigate(to as string)
          }
        />
        {screenView === 'mobile' ? (
          <h1 className="text-[18px] font-[700] text-primary">{children}</h1>
        ) : (
          <Title>{children}</Title>
        )}
      </div>
    );
  };

  const renderTitleOrSearch = (tab: string): ReactNode => {
    const pathname = location.pathname || '';
    const back = getBackConfig(pathname);
    const baseTitle = getPathTitle(pathname);
    const title = resolveTitle(pathname, baseTitle);

    switch (tab) {
      case 'Home':
        return <Title>Home</Title>;
      case 'Banking':
        if (back) return <BackTitle to={back.to}>{back.title}</BackTitle>;
        return <Title>{title}</Title>;
      case 'Finances':
        return <Title>{title}</Title>;
      case 'Vendor Marketplace':
        return (
          <Title>
            {screenView === 'mobile' ? 'Vendors' : 'Vendor Marketplace'}
          </Title>
        );
      case 'Drivers':
        return <Title>{title}</Title>;
      case 'Assets':
        if (back) return <BackTitle to={back.to}>{back.title}</BackTitle>;
        return <Title>{title}</Title>;
      default:
        return <Title>{title}</Title>;
    }
  };

  const renderTitleOrSearchTablet = () => {
    const pathname = location.pathname || '';
    const back = getBackConfig(pathname);
    const baseTitle = getPathTitle(pathname);
    const title = resolveTitle(pathname, baseTitle);

    if (
      screenView === 'mobile' &&
      (title === 'Home' ||
        title === 'Demo' ||
        pathname === '/demo' ||
        pathname === '/demo/')
    ) {
      return <h1 className="text-[18px] font-[700] text-primary">Home</h1>;
    } else if (screenView === 'mobile' && title === 'Vendor Marketplace') {
      return <h1 className="text-[18px] font-[700] text-primary">Vendors</h1>;
    } else if (back && back?.title === 'Odometer History') {
      back.title = 'History';
    } else if (back && back?.title === 'Request a Cash Advance') {
      back.title = 'New Request';
    } else if (screenView === 'mobile' && title === 'Driver Performance') {
      return (
        <h1 className="text-[16px] font-[700] text-primary">
          Driver Performance
        </h1>
      );
    }

    if (back) {
      return <BackTitle to={back.to}>{back.title}</BackTitle>;
    }

    return <h1 className="text-[18px] font-[700] text-primary">{title}</h1>;
  };

  return (
    <>
      {screenView === 'desktop' ? (
        <div className="w-full max-w-[1148px] items-center justify-center border-b border-gray-200 align-middle">
          <div className="flex w-full flex-row items-center justify-between bg-transparent align-middle">
            <div className="flex w-[35%] items-center pb-[16px]">
              {renderTitleOrSearch(currentTab)}
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() =>
                  (window.location.href = 'https://tachusa.com/select-role')
                }
                className="flex items-center space-x-2 rounded-full bg-ctadarkgreen px-4 py-2 text-white hover:bg-emerald-700"
              >
                <SignIn size={20} weight="bold" />
                <span className="text-sm font-semibold">Sign Up</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative max-h-[103px] w-full items-center justify-center align-middle">
          <img
            src="/TachLogo.svg"
            alt="Tach Logo"
            className="absolute left-3 top-4 w-20"
          />
          <List
            size={32}
            className="absolute right-3 top-3 text-base hover:cursor-pointer"
            onClick={() => setShowSideNav(!showSideNav)}
          />
          <div className="flex w-full flex-row items-center justify-center bg-white px-4 py-3 align-middle">
            <div className="flex items-center">
              {renderTitleOrSearchTablet()}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DemoTopBar;
